import { FC, useMemo } from 'react'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'

import { Product } from '../../utils/types/Product'
import { getMediumPreview, imageLoader } from '../../utils/images'
import { getProductImageAltText } from '../../lib/text'
import useUrls from '../../services/useUrls'
import { getPathName } from '../../lib/product'
import Carousel from '../Layouts/Carousel'
import ExternalLinkIcon from '../Icons/ExternalLinkIcon'
import { VERY_LIGHT_GRAY } from '../../utils/constants'

import styles from '../../styles/CompatibleProducts.module.less'

interface CompatibleProductsProps {
  product: Product
}

const CompatibleProducts: FC<CompatibleProductsProps> = ({ product }) => {
  const { t } = useTranslation('product')
  const { urlT } = useUrls()

  // Get related products from relationships
  const { replacementProducts, accessoryProducts } = product

  // Combine current product with related products
  const allProducts = useMemo(() => {
    if (!replacementProducts?.length && !accessoryProducts?.length) return []
    // remove duplicates
    // replacementProducts and accessoryProducts can have same product
    const uniqueProducts = Array.from(new Map([
      ...(replacementProducts || []),
      ...(accessoryProducts || []),
    ].map((p) => [p.id, p])).values())
    return uniqueProducts
  }, [replacementProducts, accessoryProducts, product])

  const renderProductCards = (products: Product[]) => (
    <Carousel
      gap={20}
      hideArrowsFade
      hideArrows={allProducts?.length <= 2}
    >
      {products.map((productItem) => {
        const productUrl = urlT(getPathName(productItem, t))
        const imageAltText = getProductImageAltText(productItem)

        return (
          <Link
            href={productUrl}
            key={productItem.id}
            className={styles['product-card']}
            target="_blank"
          >
            <div className={styles['product-code-row']}>
              <span className={styles['product-code']}>{productItem['product.code']}</span>
              <span className={styles['link-icon']}><ExternalLinkIcon fill={VERY_LIGHT_GRAY} /></span>
            </div>
            <div className={styles['product-image-container']}>
              <Image
                loader={imageLoader}
                src={getMediumPreview(productItem?.media?.[0])}
                alt={imageAltText}
                className={styles['product-image']}
                width={80}
                height={80}
              />
            </div>
            <div className={styles['product-label']}>
              {productItem['product.label']}
            </div>
          </Link>
        )
      })}
    </Carousel>
  )

  if (!allProducts?.length) {
    return null
  }

  return (
    <div className={styles['compatible-section']}>
      {renderProductCards(allProducts)}
    </div>
  )
}

export default CompatibleProducts
